import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Contact, Mail, MapIcon, Phone } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb';
import Heading from '../../components/Heading';
import LayoutGuest from '../../components/layouts/LayoutGuest';
import { Button } from '../../components/ui/button';
import { BASE_URL } from '../../routes/routeConstant';
import useCompanyProfile from '../../stores/useCompanyProfiles';
import Link from '../../components/ui/Link';
import { LanguageContext } from '../../contexts/LanguageContext';

export default function ContactUsPage() {
  const { profile } = useCompanyProfile();
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const breadcrumbPath = [{ name: t('navigation.home'), link: BASE_URL }, { name: t('navigation.contact') }];

  const phoneNumber = profile?.whatsapp_pr;
  const message = lang
    ? `Halo, saya tertarik dengan layanan Anda. Mohon informasi lebih lanjut mengenai pilihan layanan, harga, dan ketersediaan. Terima kasih!`
    : `Hello, I am interested in your services. Could you please provide more details about the available options, pricing, and availability? Thank you!`;
  const whatsappUrl = `https://wa.me/+62${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <LayoutGuest className="max-w-screen-xl">
      <section className="flex flex-col items-center h-full w-full gap-4 p-2 my-4 md:my-8">
        <div className="flex flex-col items-center gap-1">
          <Heading>{t('navigation.contact')}</Heading>
          <Breadcrumb backStatus="hidden" paths={breadcrumbPath} />
        </div>
        <p className="text-center w-[90%] mb-4 sm:text-base text-sm">{t('tagline.contact')}</p>
        <div className="flex md:flex-row flex-col gap-8 justify-between w-full">
          <div className="flex flex-col gap-8 w-full">
            <div key="location" className="flex animate-fade-in-up duration-300 flex-col gap-2">
              <Heading className="flex gap-2 items-center  duration-200">
                <MapIcon /> {t('general.location')}
              </Heading>
              <div className="sm:text-base text-sm ">{profile?.location_pr}</div>
            </div>
            <div key="contact" className="flex animate-fade-in-up duration-500 flex-col gap-2">
              <Heading className="flex gap-2 items-center  duration-200">
                <Contact /> {t('navigation.contact')}
              </Heading>
              <div className="flex sm:flex-row flex-col-reverse gap-2 sm:items-center ">
                <div className="border-border border p-1 rounded-lg w-fit">
                  <img src="/assets/msa-barcode.png" alt="msa-barcode" className="h-24" />
                </div>
                <ul className="flex flex-col gap-1">
                  <li>
                    <Button
                      variant="link"
                      className="hover:no-underline hover:cursor-text select-text h-fit text-sm md:text-base p-0"
                    >
                      <Phone />
                      {profile?.phone_pr || ''}
                    </Button>
                  </li>
                  <li>
                    <Link to={whatsappUrl} target="_blank">
                      <Button
                        variant="link"
                        className=" h-fit text-sm md:text-base p-0 hover:text-secondary hover:no-underline"
                      >
                        <img alt="whatsapp logo" src="/assets/Whatsapp.svg" className="w-4 brightness-[10%]" />
                        +62{profile?.whatsapp_pr || ''}
                      </Button>
                    </Link>
                  </li>
                  <li>
                    <Button
                      variant="link"
                      className=" hover:no-underline hover:cursor-text select-text h-fit text-sm md:text-base p-0"
                    >
                      <Mail />
                      {profile?.email_pr || ''}
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
            <div key="operational" className="flex animate-fade-in-up duration-700 flex-col gap-2">
              <Heading className="flex gap-2 items-center  duration-200">
                <Clock /> {t('general.operational')}
              </Heading>
              {lang ? (
                <div
                  className="sm:text-base font-medium text-sm "
                  dangerouslySetInnerHTML={{ __html: profile?.operational || '' }}
                />
              ) : (
                <div
                  className="sm:text-base font-medium text-sm "
                  dangerouslySetInnerHTML={{
                    __html: profile?.operational_en ? profile?.operational_en : profile?.operational || '',
                  }}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <div
              className="w-full htmlmap p-2 border border-border rounded-lg overflow-hidden"
              dangerouslySetInnerHTML={{ __html: profile?.html_map || '' }}
            />
          </div>
        </div>
      </section>
    </LayoutGuest>
  );
}
